<script setup lang="ts">
import { omit } from 'lodash-es';
import { computed, ref } from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type {
  ISettingsInformationItemPayload,
  ISettingsInformationItemSavePayload,
} from '@modules/general-settings/submodules/information/types';
import VCard from '@components/card/VCard/VCard.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION,
} from '@modules/general-settings/submodules/information/constants';
import useSettingsInformationSaveMutation
  from '@modules/general-settings/submodules/information/mutations/useSettingsInformationSaveMutation';
import useSettingsInformationItemQuery
  from '@modules/general-settings/submodules/information/queries/useSettingsInformationItemQuery';
import { useI18n, useLanguages } from '@modules/i18n/composables';
import { useI18nStore } from '@modules/i18n/store';
import useStaticPageConfig from '@modules/static-pages/composables/useStaticPageConfig';

const { interfaceLanguages } = useLanguages();
const { getTranslation } = useI18n();
const { currentLocale } = useI18nStore();
const { staticPageOptions } = useStaticPageConfig();

const formValues = ref<ISettingsInformationItemSavePayload>({
  language: currentLocale,
  page: staticPageOptions.value[0]?.value,
  content: '',
});
const informationItemPayload = computed<ISettingsInformationItemPayload>(() => omit(formValues.value, 'content'));

const { data: informationItem, isLoading: isLoadingInformationItem } = useSettingsInformationItemQuery(informationItemPayload);

const {
  mutateAsync: savePageItem,
  isLoading: isLoadingSubmission,
  errors,
  clearError,
} = useSettingsInformationSaveMutation();

const formSchema = computed<FormSchema<ISettingsInformationItemSavePayload>>(() => ({
  language: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION, 'LANGUAGE').value,
    options: interfaceLanguages.value,
    value: currentLocale,
    required: true,
  },
  page: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION, 'TYPE').value,
    options: staticPageOptions.value,
    value: formValues.value.page,
    required: true,
  },
  content: {
    type: EFormInputTypes.TEXT_EDITOR,
    label: formValues.value.page ? getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION, `PAGE_${formValues.value.page}`).value : undefined,
    value: informationItem.value?.content,
  },
}));

const formGroups = computed<FormGroup<ISettingsInformationItemSavePayload>[]>(() => ([
  {
    items: ['language', 'page'],
  },
  {
    items: ['content'],
    isLoading: isLoadingInformationItem.value,
  },
]));

function handleChange<
  T extends ISettingsInformationItemSavePayload,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined, result: FormResult<T>): void {
  formValues.value = result.values;
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsInformationPage'">
    <VForm
      :schema="formSchema"
      :groups="formGroups"
      :is-loading="isLoadingSubmission"
      :custom-errors="errors"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
      @interacted="clearError"
      @change="handleChange"
      @submit="savePageItem"
    />
  </VCard>
</template>
